import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useNavigate } from "react-router-dom"
import ProtocolStatsSummary from "../../../components/ProtocolStatsSummary"
import { ProtocolData } from "../../../services/studyProtocolApi"

interface ProtocolPendingReviewProps {
  /**
   * Study ID for navigation links.
   */
  studyId: string
  /**
   * Extracted protocol data to summarize.
   */
  data: ProtocolData | null
}

/**
 * Displayed when protocol extraction is complete but not yet approved.
 *
 * Shows a summary of extracted data (visit and assessment counts) and prompts the DAE to review and
 * submit the protocol before it can be used in DTAs.
 */
const ProtocolPendingReview = ({ studyId, data }: ProtocolPendingReviewProps) => {
  const navigate = useNavigate()

  const visitCount =
    data?.parts?.reduce(
      (sum, p) => sum + (p.periods?.reduce((s, per) => s + (per.visits?.length || 0), 0) || 0),
      0,
    ) || 0
  const assessmentCount =
    data?.parts?.reduce((sum, p) => sum + (p.assessments?.length || 0), 0) || 0

  return (
    <Container
      header={
        <Header description="Review the extracted data and make any corrections before submitting.">
          <SpaceBetween direction="horizontal" size="xs">
            <Icon name="status-positive" size="big" />
            <span>Extraction Complete - Review & Submit</span>
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="l">
        <ProtocolStatsSummary
          stats={[
            { value: visitCount, label: "Visits" },
            { value: assessmentCount, label: "Assessments" },
          ]}
          variant="compact"
        />

        <Box>
          <Button
            variant="primary"
            iconName="edit"
            onClick={() => navigate(`/studies/${studyId}/protocol/edit`)}
            fullWidth
          >
            Review & Submit
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  )
}

export default ProtocolPendingReview
